import React, { useState, useMemo } from 'react';
import { Interview } from '@/types/interview';
import PendingInterviewsTable from './PendingInterviewsTable';
import ViewInterviewDialog from './ViewInterviewDialog';
import EditInterviewDialog from './EditInterviewDialog';
import EmptyInterviewsList from './EmptyInterviewsList';
import FolderTabs from './FolderTabs';
import { toast } from 'sonner';
import { InterviewService } from '@/services/interview/interviewService';
import { useQueryClient } from '@tanstack/react-query';
import ConfirmationDialog from './ConfirmationDialog';
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from 'react-i18next';
import { useInterviewFolders } from '@/hooks/interview/useInterviewFolders';

interface PendingInterviewsListProps {
  interviews: Interview[];
  onPublish: (interviewId: string) => Promise<void>;
  onReject: (interviewId: string) => Promise<void>;
}

const PendingInterviewsList: React.FC<PendingInterviewsListProps> = ({ 
  interviews, 
  onPublish,
  onReject
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [sendingToFeederId, setSendingToFeederId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);
  const [isFeederConfirmOpen, setIsFeederConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [interviewToProcess, setInterviewToProcess] = useState<string | null>(null);

  // Folder state
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const {
    folders,
    assignments,
    createFolder,
    renameFolder,
    deleteFolder,
    assignToFolder,
    removeFromFolder,
    getInterviewIdsForFolder,
    getFolderIdsForInterview,
  } = useInterviewFolders();

  // Get all interview IDs that are assigned to any folder
  const interviewIdsInAnyFolder = useMemo(() => {
    const ids = new Set<string>();
    for (const folder of folders) {
      for (const id of getInterviewIdsForFolder(folder.id)) {
        ids.add(id);
      }
    }
    return ids;
  }, [folders, assignments]);

  // Filter interviews by active folder — main tab hides folder-assigned articles
  const filteredInterviews = useMemo(() => {
    if (activeFolderId === null) {
      return interviews.filter(i => !interviewIdsInAnyFolder.has(i.id));
    }
    const idsInFolder = getInterviewIdsForFolder(activeFolderId);
    return interviews.filter(i => idsInFolder.includes(i.id));
  }, [interviews, activeFolderId, assignments, interviewIdsInAnyFolder]);

  // Folder counts
  const folderCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    const interviewIds = new Set(interviews.map(i => i.id));
    for (const folder of folders) {
      counts[folder.id] = getInterviewIdsForFolder(folder.id).filter(id => interviewIds.has(id)).length;
    }
    return counts;
  }, [folders, interviews, assignments]);

  const handleToggleFolder = (interviewId: string, folderId: string, isAssigned: boolean) => {
    if (isAssigned) {
      removeFromFolder.mutate({ interviewIds: [interviewId], folderId });
    } else {
      assignToFolder.mutate({ interviewIds: [interviewId], folderId });
    }
  };

  const handleViewInterview = (interview: Interview) => {
    setSelectedInterview(interview);
    setIsViewDialogOpen(true);
  };

  const handleEditInterview = (interview: Interview) => {
    setSelectedInterview(interview);
    setIsEditDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setIsViewDialogOpen(false);
    setSelectedInterview(null);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setSelectedInterview(null);
  };

  const confirmPublish = (interviewId: string) => {
    setInterviewToProcess(interviewId);
    setIsPublishConfirmOpen(true);
  };

  const confirmSendToFeeder = (interviewId: string) => {
    setInterviewToProcess(interviewId);
    setIsFeederConfirmOpen(true);
  };

  const confirmDelete = (interviewId: string) => {
    setInterviewToProcess(interviewId);
    setIsDeleteConfirmOpen(true);
  };

  const handlePublish = async (interviewId: string) => {
    if (publishingId) return;
    try {
      setPublishingId(interviewId);
      await onPublish(interviewId);
    } finally {
      setTimeout(() => setPublishingId(null), 500);
    }
  };

  const handleSendToFeeder = async (interviewId: string) => {
    if (sendingToFeederId) return;
    try {
      setSendingToFeederId(interviewId);
      toast.loading(t('interviewManagement.messages.sendingToQueue'), { id: 'sending-to-feeder' });
      const result = await InterviewService.markForFeeder(interviewId);
      if (result) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['pendingInterviews'] }),
          queryClient.invalidateQueries({ queryKey: ['feederInterviews'] })
        ]);
        toast.success(t('interviewManagement.messages.sentToQueue'), { id: 'sending-to-feeder' });
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ['pendingInterviews'] }),
          queryClient.refetchQueries({ queryKey: ['feederInterviews'] })
        ]);
      } else {
        toast.error(t('interviewManagement.messages.couldNotSendToQueue'), { id: 'sending-to-feeder' });
        throw new Error(t('interviewManagement.messages.couldNotSendToQueue'));
      }
    } catch (error) {
      console.error('Feil ved sending til køen:', error);
      toast.error(`${t('interviewManagement.messages.couldNotSendToQueue')}: ${error instanceof Error ? error.message : t('interviewManagement.messages.unknownError')}`, { id: 'sending-to-feeder' });
      throw error;
    } finally {
      setTimeout(() => setSendingToFeederId(null), 500);
    }
  };

  const handleDelete = async (interviewId: string) => {
    setIsDeleteConfirmOpen(false);
    setIsViewDialogOpen(false);
    setIsEditDialogOpen(false);
    if (deletingId) return;
    try {
      setDeletingId(interviewId);
      toast.loading(t('interviewManagement.messages.deletingInterview'), { id: 'deleting-interview' });
      const result = await InterviewService.deleteInterview(interviewId);
      if (result) {
        await queryClient.invalidateQueries({ queryKey: ['pendingInterviews'] });
        await queryClient.invalidateQueries({ queryKey: ['feederInterviews'] });
        await queryClient.invalidateQueries({ queryKey: ['interviews'] });
        toast.success(t('interviewManagement.messages.interviewDeleted'), { id: 'deleting-interview' });
        await queryClient.refetchQueries({ queryKey: ['pendingInterviews'] });
        await queryClient.refetchQueries({ queryKey: ['feederInterviews'] });
        await queryClient.refetchQueries({ queryKey: ['interviews'] });
      } else {
        try {
          const { error } = await supabase.from('interviews').delete().eq('id', interviewId);
          if (error) throw new Error(`Could not delete interview: ${error.message}`);
          await queryClient.invalidateQueries();
          toast.success(t('interviewManagement.messages.alternateDeleteSuccess'), { id: 'deleting-interview' });
        } catch (altError) {
          throw new Error(t('interviewManagement.messages.couldNotDelete'));
        }
      }
    } catch (error) {
      console.error('Error deleting interview:', error);
      toast.error(`${t('interviewManagement.messages.couldNotDelete')}: ${error instanceof Error ? error.message : t('interviewManagement.messages.unknownError')}`, { id: 'deleting-interview' });
    } finally {
      setDeletingId(null);
    }
  };

  if (interviews.length === 0) {
    return <EmptyInterviewsList />;
  }

  return (
    <div>
      <FolderTabs
        folders={folders}
        activeFolderId={activeFolderId}
        onSelectFolder={setActiveFolderId}
        onCreateFolder={(name) => createFolder.mutate(name)}
        onRenameFolder={(id, name) => renameFolder.mutate({ id, name })}
        onDeleteFolder={(id) => deleteFolder.mutate(id)}
        folderCounts={folderCounts}
        totalCount={interviews.filter(i => !interviewIdsInAnyFolder.has(i.id)).length}
      />

      <h3 className="text-lg font-medium mb-4">
        {activeFolderId 
          ? `${folders.find(f => f.id === activeFolderId)?.name || ''} (${filteredInterviews.length})`
          : t('interviewManagement.pendingCount', { count: filteredInterviews.length })
        }
      </h3>
      
      {filteredInterviews.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          Ingen artikler i denne mappen
        </div>
      ) : (
        <PendingInterviewsTable 
          interviews={filteredInterviews}
          onViewInterview={handleViewInterview}
          onEditInterview={handleEditInterview}
          onPublish={confirmPublish}
          onSendToFeeder={confirmSendToFeeder}
          onDelete={confirmDelete}
          publishingId={publishingId}
          sendingToFeederId={sendingToFeederId}
          deletingId={deletingId}
          folders={folders}
          getFolderIdsForInterview={getFolderIdsForInterview}
          onToggleFolder={handleToggleFolder}
        />
      )}

      <ViewInterviewDialog 
        interview={selectedInterview}
        isOpen={isViewDialogOpen}
        onClose={handleCloseViewDialog}
        onPublish={handlePublish}
        onSendToFeeder={handleSendToFeeder}
        publishingId={publishingId}
        sendingToFeederId={sendingToFeederId}
      />

      <EditInterviewDialog 
        interview={selectedInterview}
        isOpen={isEditDialogOpen}
        onClose={handleCloseEditDialog}
      />

      <ConfirmationDialog
        isOpen={isPublishConfirmOpen}
        onClose={() => setIsPublishConfirmOpen(false)}
        onConfirm={async () => {
          if (interviewToProcess) {
            await handlePublish(interviewToProcess);
            setInterviewToProcess(null);
          }
          setIsPublishConfirmOpen(false);
        }}
        title={t('interviewManagement.confirmations.publishTitle')}
        description={t('interviewManagement.confirmations.publishDescription')}
        confirmText={t('interviewManagement.confirmations.confirm')}
        cancelText={t('interviewManagement.confirmations.cancel')}
      />

      <ConfirmationDialog
        isOpen={isFeederConfirmOpen}
        onClose={() => setIsFeederConfirmOpen(false)}
        onConfirm={async () => {
          if (interviewToProcess) {
            await handleSendToFeeder(interviewToProcess);
            setInterviewToProcess(null);
          }
          setIsFeederConfirmOpen(false);
        }}
        title={t('interviewManagement.confirmations.feederTitle')}
        description={t('interviewManagement.confirmations.feederDescription')}
        confirmText={t('interviewManagement.confirmations.confirm')}
        cancelText={t('interviewManagement.confirmations.cancel')}
      />
      
      <ConfirmationDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={() => {
          if (interviewToProcess) {
            handleDelete(interviewToProcess);
            setInterviewToProcess(null);
          }
        }}
        title={t('interviewManagement.confirmations.deleteTitle')}
        description={t('interviewManagement.confirmations.deleteDescription')}
        confirmText={t('interviewManagement.delete')}
        cancelText={t('interviewManagement.confirmations.cancel')}
      />
    </div>
  );
};

export default PendingInterviewsList;
